import type { SpreadLayoutId, TextBlock } from '@kitup/book-schema';
import { request } from './client.js';

export interface StorySpreadDto {
  readonly index: number;
  readonly textRu: string;
  readonly promptEn: string;
  readonly animationEn: string;
  readonly characterIds: readonly string[];
  readonly thingIds: readonly string[];
  readonly locationId: string | null;
  readonly promptStatus: 'queued' | 'running' | 'ready' | 'failed';
  readonly promptError: string | null;
}

export interface StoryDraftWarningDto {
  readonly code: 'character_underused';
  readonly characterId: string;
  readonly characterName: string;
  readonly sceneCount: number;
}

export interface StoryDraftDto {
  readonly id: string;
  readonly bookId: string;
  readonly source: 'manual' | 'ai';
  readonly brief: Readonly<Record<string, unknown>>;
  readonly model: string | null;
  readonly titleRu: string | null;
  readonly parsed: readonly StorySpreadDto[] | null;
  readonly status: 'queued' | 'running' | 'ready' | 'failed';
  readonly error: string | null;
  readonly warnings: readonly StoryDraftWarningDto[];
  readonly isApplied: boolean;
  readonly appliedAt: string | null;
  readonly createdAt: string;
}

export interface SceneDto {
  readonly id: string;
  readonly bookId: string;
  readonly index: number;
  readonly kind: 'cover' | 'spread' | 'backCover';
  readonly layoutId: SpreadLayoutId | null;
  readonly textRu: string;
  readonly textBlocks: readonly TextBlock[];
  readonly bandImageRatio: number | null;
  readonly promptEn: string;
  readonly animationPromptEn: string;
  readonly characterIds: readonly string[];
  readonly thingIds: readonly string[];
  readonly locationId: string | null;
  readonly imageAssetId: string | null;
  readonly videoAssetId: string | null;
  readonly imageSpec: unknown | null;
  readonly imageStale: boolean;
  readonly status: 'draft' | 'queued' | 'generating' | 'ready' | 'failed';
  readonly lastError: string | null;
  readonly promptStatus: 'queued' | 'running' | 'ready' | 'failed';
  readonly promptError: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * Бриф генерации: свободный текст пожеланий. Повод, стоп-темы и обязательные
 * пункты API берёт из книги — их формы скрыты (docs/05-TECH-DEBT.md).
 */
export interface GenerateStoryInput {
  readonly mainIdea: string;
}

/** Правка текста; сервер асинхронно пересобирает визуальный промпт и привязки. */
export interface UpdateStoryDraftInput {
  readonly spreads: readonly {
    readonly index: number;
    readonly textRu: string;
    readonly regeneratePrompt?: boolean;
  }[];
}

export interface UpdateSceneInput {
  readonly textRu?: string;
  readonly promptEn?: string;
  readonly animationPromptEn?: string;
  readonly layoutId?: SpreadLayoutId | null;
  readonly bandImageRatio?: number | null;
  readonly characterIds?: readonly string[];
  readonly thingIds?: readonly string[];
  readonly locationId?: string | null;
  readonly textBlocks?: readonly TextBlock[];
  readonly regeneratePrompt?: boolean;
}

export const storyApi = {
  generate: (bookId: string, body: GenerateStoryInput) =>
    request<{ draftId: string; generationId: string; status: 'queued' }>(
      `/books/${bookId}/story/generate`,
      { method: 'POST', body },
    ),
  drafts: (bookId: string) => request<{ items: StoryDraftDto[] }>(`/books/${bookId}/story/drafts`),
  updateDraft: (draftId: string, body: UpdateStoryDraftInput) =>
    request<StoryDraftDto>(`/story/drafts/${draftId}`, { method: 'PATCH', body }),
  apply: (draftId: string, coverTitle: string, confirmReplace = false) =>
    request<{ items: SceneDto[] }>(`/story/drafts/${draftId}/apply`, {
      method: 'POST',
      body: { coverTitle, confirmReplace },
    }),
  scenes: (bookId: string) => request<{ items: SceneDto[] }>(`/books/${bookId}/scenes`),
  createScene: (bookId: string, body: UpdateSceneInput) =>
    request<SceneDto>(`/books/${bookId}/scenes`, { method: 'POST', body }),
  updateScene: (sceneId: string, body: UpdateSceneInput) =>
    request<SceneDto>(`/scenes/${sceneId}`, { method: 'PATCH', body }),
  deleteScene: (sceneId: string) =>
    request<{ id: string; deleted: true }>(`/scenes/${sceneId}`, { method: 'DELETE' }),
  reorder: (bookId: string, sceneIds: readonly string[]) =>
    request<{ items: SceneDto[] }>(`/books/${bookId}/scenes/reorder`, {
      method: 'POST',
      body: { sceneIds },
    }),
};
